/* eslint-disable no-case-declarations */
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Select,
  SelectItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { toast } from "react-toastify";
import { FaBars, FaDownload, FaChartLine } from "react-icons/fa6";

import { FinanceRenterChip } from "@/app/components/chips/renterChip";
import { FinanceOfficeChip } from "@/app/components/chips/officeChip";
import { FinanceRentedOfficeChip } from "@/app/components/chips/rentedOfficeChip";
import { CreateBillFromFinanceModal } from "@/app/components/modals/bill/createBillFromFinanceModal";

import {
  formatNumber,
  getBaseUrl,
  getCurrentMonth,
  getMonthInEnglish,
  getMonthNumber,
  getRussianMonthFromEnglish,
  summaryValue,
} from "@/utils";
import { ActualPayment, RentPlan, SummaryReport } from "@/api/analytics";
import { api } from "@/api";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useAppSelector } from "@/hooks/useAppSelector";
import { NewLocationHandbook } from "@/app/components/handbooks/locationHandbook";
import { NewYearHandbook } from "@/app/components/handbooks/service/yearHandbook";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { Agreement } from "@/api/agreements";
import { FinanceRequisiteChip } from "@/app/components/chips/requisiteChip";
import { SummaryChart } from "./widgets/summaryChart";
import { EstimateModal } from "./widgets/estimateModal";
import { EstimateChart } from "./widgets/estimateChart";
import { EstimateHistory } from "@/api/analytics";
import { Billing, BillingDepositState, BillingType } from "@/api/billing";
import { Office } from "@/api/offices";
import { Renter } from "@/api/renters";
import { Requisite } from "@/api/requisites/requisites";
import {
  FinancePaymentChip,
  FinanceDepositChip,
} from "@/app/components/chips/paymentChip";
import { PromptPaymentModal } from "@/app/components/chips/modals/promptPaymentModal";
import { EditBillModal } from "@/app/components/modals/bill/editBillModal";
import { EditAgreementModal } from "@/app/components/modals/agreements/editAgreementModal";
import { EditOfficeModal } from "@/app/components/modals/office/editOfficeModal";
import { RenterModal } from "@/app/components/chips/modals/renterModal";
import { EditRequisiteModal } from "@/app/components/modals/requisites/editRequisitesModal";

interface SummaryRow {
  payment_plan: number;
  payment_actual: number;
  deposit: number;
  sale: number;
  potential_earnings: number;
  workplaces: number;
  price_per_workplace: number;
  potential_price_per_workplace: number;
  price_per_sqm: number;
  potential_price_per_sqm: number;
  total_square_meters: number;
}

export function FinancePage() {
  const handleError = useErrorHandling();
  const firstLocation = useAppSelector(
    (state) => state.handbooks.location[0]?.id || 0,
  );
  const offices = useAppSelector((state) => state.handbooks.office);
  const businessProfile = useAppSelector(
    (state) => state.businessProfile.businessProfile,
  );
  const rentedOffices = useAppSelector((state) => state.handbooks.agreements);
  const payments = useAppSelector((state) => state.handbooks.payments);
  const deposits = useAppSelector((state) => state.handbooks.deposits);
  const officeTypes = useAppSelector((state) => state.handbooks.officeType);

  const [activeTab, setActiveTab] = useState<string>(
    getMonthInEnglish(getCurrentMonth()),
  );
  const [rawData, setRawData] = useState<SummaryReport | null>(null);
  const [activeLocation, setActiveLocation] = useState<string>(
    firstLocation.toString(),
  );
  const [activeYear, setActiveYear] = useState<string>(
    String(new Date().getFullYear()),
  );
  const [createPaymentProps, setCreatePaymentProps] = useState<{
    rentedOffice: Agreement | null;
    month: number;
    year: string;
    customPrice?: number;
    type?: "payment" | "deposit";
    targetOffice?: number;
  }>({
    rentedOffice: null,
    month: 1,
    year: activeYear,
    type: "payment",
    targetOffice: undefined,
  });

  const [activeRentContract, setActiveRentContract] =
    useState<Agreement | null>(null);
  const [activePayment, setActivePayment] = useState<Billing | null>(null);
  const [customActivePayment, setCustomActivePayment] =
    useState<Billing | null>(null);
  const [activeViewOffice, setActiveViewOffice] = useState<Office | null>(null);
  const [activeViewRenter, setActiveViewRenter] = useState<Renter | null>(null);
  const [activeViewRequisite, setActiveViewRequisite] =
    useState<Requisite | null>(null);
  const [activeViewPayments, setActiveViewPayments] = useState<ActualPayment[]>(
    [],
  );
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set([]));

  const editRentContractDisclosure = useDisclosure();
  const createPaymentDisclosure = useDisclosure();
  const editPaymentDisclosure = useDisclosure();
  const editCustomPaymentDisclosure = useDisclosure();
  const editOfficeDisclosure = useDisclosure();
  const editRenterDisclosure = useDisclosure();
  const editRequisiteDisclosure = useDisclosure();
  const promptPaymentDisclosure = useDisclosure();
  const estimateDisclosure = useDisclosure();

  const [estimateOfficeId, setEstimateOfficeId] = useState<number | null>(null);
  const [chartMode, setChartMode] = useState<string>("revenue");
  const [marketHistory, setMarketHistory] = useState<EstimateHistory | null>(
    null,
  );
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const defaultTo = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [marketDateFrom, setMarketDateFrom] = useState(defaultFrom);
  const [marketDateTo, setMarketDateTo] = useState(defaultTo);

  useEffect(() => {
    const locId = Number(activeLocation);
    if (chartMode !== "market" || !Number.isFinite(locId) || locId <= 0) return;
    api.analytics
      .getHistory(locId, marketDateFrom, marketDateTo)
      .then(setMarketHistory)
      .catch(() => setMarketHistory(null));
  }, [chartMode, activeLocation, marketDateFrom, marketDateTo]);

  const downloadReport = useCallback(() => {
    fetch(
      getBaseUrl() +
        "/business/analytics/summary/export?" +
        new URLSearchParams({
          business_profile_id: businessProfile.id.toString(),
          period: getRussianMonthFromEnglish(activeTab),
          year: activeYear,
          location_id: activeLocation,
        }),
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("access_token"),
        },
      },
    )
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${activeYear}-${activeTab}-${activeLocation}-report-${Math.round(Date.now() / 1000)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        toast.success("Отчёт успешно экспортирован!");
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [businessProfile.id, activeTab, activeYear, activeLocation, handleError]);

  const downloadBillsReport = useCallback(() => {
    fetch(
      getBaseUrl() +
        "/business/billings/report?" +
        new URLSearchParams({
          business_profile_id: businessProfile.id.toString(),
        }),
      {
        method: "POST",
        body: JSON.stringify({
          period: getRussianMonthFromEnglish(activeTab),
          year: Number(activeYear),
          location: Number(activeLocation),
        }),
        headers: {
          Authorization: "Bearer " + localStorage.getItem("access_token"),
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bills-${activeYear}-${activeTab}-${activeLocation}-report-${Math.round(Date.now() / 1000)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        toast.success("Отчёт успешно экспортирован!");
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [businessProfile.id, activeTab, activeYear, activeLocation, handleError]);

  useEffect(() => {
    setActiveLocation(firstLocation.toString());
    setActiveYear(String(new Date().getFullYear()));
    setActiveTab(getMonthInEnglish(getCurrentMonth()));
  }, [firstLocation]);

  const fetchData = useCallback(async () => {
    if (!activeTab || Number(activeLocation) === 0) return;

    try {
      const res = await api.analytics.summary(
        getRussianMonthFromEnglish(activeTab),
        activeYear,
        Number(activeLocation),
        Array.from(activeTypes).join(","),
      );
      setRawData(res.data);

      loadHandbooks({
        handbooks: ["agreements", "billings"],
        arguments: [
          {
            location: activeLocation,
            year: activeYear,
            period: getRussianMonthFromEnglish(activeTab),
          },
        ],
      });
    } catch (err: any) {
      const { errorMessage } = handleError(err);
      toast.error(errorMessage);
    }
  }, [
    activeTab,
    activeLocation,
    activeYear,
    handleError,
    setRawData,
    activeTypes,
  ]);

  useEffect(() => {
    fetchData();
  }, [
    createPaymentDisclosure.isOpen,
    editRentContractDisclosure.isOpen,
    editPaymentDisclosure.isOpen,
    editOfficeDisclosure.isOpen,
    editRenterDisclosure.isOpen,
    editRequisiteDisclosure.isOpen,
    editCustomPaymentDisclosure.isOpen,
    activeYear,
    activeLocation,
    activeTab,
    fetchData,
  ]);

  const officeChipCallback = useCallback(
    (item: Office) => {
      setActiveViewOffice(item);
      window.open(`/dashboard/offices/${item.id}/edit`, "_blank");
    },
    [setActiveViewOffice],
  );

  const renterChipCallback = useCallback(
    (item: Renter) => {
      setActiveViewRenter(item);
      editRenterDisclosure.onOpen();
    },
    [setActiveViewRenter, editRenterDisclosure],
  );

  const requisiteChipCallback = useCallback(
    (item: Requisite) => {
      setActiveViewRequisite(item);
      editRequisiteDisclosure.onOpen();
    },
    [setActiveViewRequisite, editRequisiteDisclosure],
  );

  const rentContractChipCallback = useCallback(
    (item: Agreement) => {
      setActiveRentContract(item);
      editRentContractDisclosure.onOpen();
    },
    [setActiveRentContract, editRentContractDisclosure],
  );

  const promptPaymentCallback = useCallback(
    (rentedOfficeId: number, item: ActualPayment[]) => {
      const rentedOffice = rentedOffices.find((el) => el.id == rentedOfficeId)!;

      setActiveRentContract(rentedOffice);
      setActiveViewPayments(item);
      promptPaymentDisclosure.onOpen();
    },
    [rentedOffices, promptPaymentDisclosure],
  );

  const handleDepositChipClick = useCallback(
    async (rentId: number, item: RentPlan) => {
      try {
        const res = await api.billings.getDeposits(rentId);
        const depositPayments: ActualPayment[] = res.data.map((billing) => ({
          billing_id: billing.id,
          office_id: item.office_id,
          renter_id: item.renter_id ?? 0,
          billing_type: BillingType.Deposite,
          total_paid: billing.amount,
          office_name: item.office_name,
          renter_name: item.renter_name || "",
          billing_number: billing.number,
          paid: billing.paid,
          agreement_id: rentId,
        }));

        setActiveRentContract(
          rentedOffices.find((el) => el.id == rentId) || null,
        );
        setActiveViewPayments(depositPayments);
        promptPaymentDisclosure.onOpen();
      } catch (err: any) {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      }
    },
    [rentedOffices, promptPaymentDisclosure, handleError],
  );

  const handleCreatePayment = useCallback(() => {
    setCreatePaymentProps({
      rentedOffice: activeRentContract,
      month: getMonthNumber(activeTab),
      year: activeYear,
      targetOffice: activeViewOffice?.id,
    });

    promptPaymentDisclosure.onClose();
    createPaymentDisclosure.onOpen();
  }, [
    activeRentContract,
    activeTab,
    activeYear,
    createPaymentDisclosure,
    promptPaymentDisclosure,
    activeViewOffice,
  ]);

  const handlePaymentSelection = useCallback(
    (payment: ActualPayment) => {
      setActivePayment(
        payments.find((p) => p.id == payment.billing_id) ?? null,
      );
      api.billings
        .get(payment.billing_id)
        .then((bill) => {
          setCustomActivePayment(bill.data);
          promptPaymentDisclosure.onClose();
          editCustomPaymentDisclosure.onOpen();
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        });
    },
    [
      editCustomPaymentDisclosure,
      handleError,
      payments,
      promptPaymentDisclosure,
    ],
  );

  const data = useMemo(() => {
    if (!rawData) return null;

    const sortedRentPlan = [...rawData.rent_plan].sort((a, b) =>
      a.office_name.localeCompare(b.office_name),
    );

    return {
      ...rawData,
      rent_plan: sortedRentPlan,
    };
  }, [rawData]);

  const summaryRow = useMemo<SummaryRow | null>(() => {
    if (!data) return null;

    return {
      ...data.summary,
      sale: 0,
    };
  }, [data]);

  const columns = useMemo(() => {
    return [
      {
        key: "office",
        label: (
          <div className="flex flex-col">
            <span>Офис</span>
          </div>
        ),
      },
      {
        key: "renter",
        label: (
          <div className="flex flex-col">
            <span>Арендатор</span>
          </div>
        ),
      },
      {
        key: "potential_income",
        label: (
          <div className="flex flex-col">
            <span>Потенциальная выручка</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.potential_earnings)}
            </span>
          </div>
        ),
      },
      {
        key: "payment_plan",
        label: (
          <div className="flex flex-col">
            <span>План оплаты</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.payment_plan)}
            </span>
          </div>
        ),
      },
      {
        key: "actual_payment",
        label: (
          <div className="flex flex-col">
            <span>Фактическая аренда</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.payment_actual)}
            </span>
          </div>
        ),
      },
      {
        key: "requisite",
        label: (
          <div className="flex flex-col">
            <span>Реквизиты</span>
          </div>
        ),
      },
      {
        key: "deposit",
        label: (
          <div className="flex flex-col">
            <span>Депозит</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.deposit)}
            </span>
          </div>
        ),
      },
      {
        key: "work_places",
        label: (
          <div className="flex flex-col">
            <span>Рабочие места</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.workplaces)}
            </span>
          </div>
        ),
      },
      {
        key: "price_per_place",
        label: (
          <div className="flex flex-col">
            <span>Цена за рабочее место</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.price_per_workplace)}
            </span>
          </div>
        ),
      },
      {
        key: "potential_price_per_place",
        label: (
          <div className="flex flex-col">
            <span>Пот. цена за раб. место</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.potential_price_per_workplace)}
            </span>
          </div>
        ),
      },
      {
        key: "square_meters",
        label: (
          <div className="flex flex-col">
            <span>Площадь (м2)</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.total_square_meters)}
            </span>
          </div>
        ),
      },
      {
        key: "price_per_square",
        label: (
          <div className="flex flex-col">
            <span>Цена за м2</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.price_per_sqm)}
            </span>
          </div>
        ),
      },
      {
        key: "potential_price_per_square",
        label: (
          <div className="flex flex-col">
            <span>Пот. цена за м2</span>
            <span className="text-foreground-900">
              {summaryValue(summaryRow?.potential_price_per_sqm)}
            </span>
          </div>
        ),
      },
    ];
  }, [summaryRow]);

  const handleDepositFallback = useCallback(
    (rentedOfficeId: number, customPrice?: number) => {
      const rentedOffice = rentedOffices.find((el) => el.id == rentedOfficeId)!;

      setCreatePaymentProps({
        rentedOffice,
        month: getMonthNumber(activeTab),
        customPrice,
        year: activeYear,
        type: "deposit",
      });

      createPaymentDisclosure.onOpen();
    },
    [rentedOffices, activeTab, activeYear, createPaymentDisclosure],
  );

  const handlePaymentFallback = useCallback(
    (rentedOfficeId: number, customPrice?: number) => {
      const rentedOffice = rentedOffices.find((el) => el.id == rentedOfficeId)!;

      setCreatePaymentProps({
        rentedOffice,
        month: getMonthNumber(activeTab),
        customPrice,
        year: activeYear,
      });

      createPaymentDisclosure.onOpen();
    },
    [rentedOffices, activeTab, activeYear, createPaymentDisclosure],
  );

  const handleRentedOfficeFallback = useCallback(() => {
    // Fallback removed - use invitations instead
  }, []);

  const getKeyValue = useCallback(
    (item: RentPlan, key: any) => {
      const numberFields: { [key: string]: (item: RentPlan) => string } = {
        work_places: (item) => formatNumber(item.workplace_count),
        price_per_place: (item) => formatNumber(item.price_per_workplace),
        potential_price_per_place: (item) =>
          formatNumber(item.potential_workspace_earning_price),
        price_per_square: (item) => formatNumber(item.price_per_sqm),
        potential_price_per_square: (item) =>
          formatNumber(item.potential_price_per_sqm),
      };

      if (numberFields[key]) {
        return numberFields[key](item);
      }

      switch (key) {
        case "potential_income":
          return (
            <FinanceOfficeChip
              officeId={item.office_id ?? 0}
              callback={officeChipCallback}
              displayName={formatNumber(item.potential_earning_price)}
            />
          );
        case "requisite":
          return (
            <FinanceRequisiteChip
              requisiteId={
                payments.find(
                  (payment) =>
                    payment.id ==
                    data?.actual_payments.find(
                      (actualPayment) =>
                        actualPayment.office_id == item.office_id,
                    )?.billing_id,
                )?.provider ||
                rentedOffices.find(
                  (rentedOffice) => rentedOffice.id == item.rent_id,
                )?.requisiteId ||
                0
              }
              callback={requisiteChipCallback}
            />
          );
        case "paid":
          return <Checkbox isDisabled defaultSelected />;
        case "office":
          return (
            <div className="flex items-center gap-1">
              <FinanceOfficeChip
                officeId={item.office_id ?? 0}
                callback={officeChipCallback}
              />
              <button
                className="text-foreground-400 hover:text-blue-500 transition-colors p-1"
                title="Оценка аренды"
                onClick={() => {
                  setEstimateOfficeId(item.office_id ?? null);
                  estimateDisclosure.onOpen();
                }}
              >
                <FaChartLine size={14} />
              </button>
            </div>
          );
        case "renter":
          return (
            <FinanceRenterChip
              renterId={item.renter_id ?? 0}
              callback={renterChipCallback}
            />
          );
        case "payment_plan":
          return (
            <FinanceRentedOfficeChip
              displayName={formatNumber(item.price)}
              rentedOfficeId={item.rent_id ?? 0}
              contractStatus={item.contract_status}
              fallbackCallback={() => {
                handleRentedOfficeFallback();
              }}
              callback={rentContractChipCallback}
            />
          );
        case "actual_payment":
          const actualPayments = data?.actual_payments.filter(
            (p) =>
              p.renter_id == item.renter_id &&
              p.office_id == item.office_id &&
              p.agreement_id == item.rent_id,
          );
          const totalPaid = actualPayments?.reduce(
            (total, item) =>
              total +
              (item.billing_type != BillingType.Deposite
                ? (item.total_paid ?? 0)
                : 0),
            0,
          );

          return (
            <FinancePaymentChip
              rentId={item.rent_id ?? 0}
              displayAmount={formatNumber(totalPaid)}
              plannedAmount={item.price}
              actualPayments={actualPayments}
              paymentId={
                data?.actual_payments.find(
                  (el) =>
                    el.office_id === item.office_id &&
                    el.billing_type == BillingType.Regular &&
                    el.agreement_id == item.rent_id,
                )?.billing_id ?? 0
              }
              fallbackCallback={() =>
                handlePaymentFallback(item.rent_id ?? 0, item.price ?? 0)
              }
              openPromptPaymentCallback={() =>
                promptPaymentCallback(item.rent_id ?? 0, actualPayments ?? [])
              }
            />
          );
        case "deposit":
          const depositAmount = item.deposit.amount;
          const depositState = item.deposit.state;

          return (
            <FinanceDepositChip
              rentId={item.rent_id ?? 0}
              displayAmount={formatNumber(depositAmount)}
              isPaid={depositState == BillingDepositState.FULLY_PAID}
              onClickCallback={(rentId) => handleDepositChipClick(rentId, item)}
              fallbackCallback={() =>
                handleDepositFallback(item.rent_id ?? 0, depositAmount)
              }
            />
          );

        case "square_meters":
          const area =
            offices.find((el) => el.id === item.office_id)?.area ?? 0;
          return formatNumber(area);
        default:
          return "н/д";
      }
    },
    [
      officeChipCallback,
      payments,
      rentedOffices,
      requisiteChipCallback,
      renterChipCallback,
      rentContractChipCallback,
      data?.actual_payments,
      promptPaymentCallback,
      deposits,
      offices,
      handleRentedOfficeFallback,
      handlePaymentFallback,
      handleDepositFallback,
      estimateDisclosure,
    ],
  );

  return (
    <>
      <CreateBillFromFinanceModal
        disclosure={createPaymentDisclosure}
        paymentProps={createPaymentProps}
      />
      <EditBillModal
        disclosure={editPaymentDisclosure}
        billing={activePayment}
      />
      <EditBillModal
        disclosure={editCustomPaymentDisclosure}
        billing={customActivePayment}
      />
      <PromptPaymentModal
        disclosure={promptPaymentDisclosure}
        payments={activeViewPayments}
        selectPayment={handlePaymentSelection}
        createPayment={handleCreatePayment}
      />
      <EditAgreementModal
        disclosure={editRentContractDisclosure}
        office={activeRentContract}
      />
      <EditOfficeModal
        disclosure={editOfficeDisclosure}
        office={activeViewOffice}
      />
      <RenterModal
        isOpen={editRenterDisclosure.isOpen}
        onOpenChange={editRenterDisclosure.onOpenChange}
        item={activeViewRenter!}
      />
      <EditRequisiteModal
        disclosure={editRequisiteDisclosure}
        requisite={activeViewRequisite}
      />
      <EstimateModal
        disclosure={estimateDisclosure}
        officeId={estimateOfficeId}
        officeName={
          offices.find((o) => o.id === estimateOfficeId)?.display_name
        }
        areaSqm={offices.find((o) => o.id === estimateOfficeId)?.area ?? 0}
        locationId={Number(activeLocation)}
      />

      <div className="flex flex-col gap-4 mt-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Tabs
              size="sm"
              variant="light"
              selectedKey={chartMode}
              onSelectionChange={(k) => setChartMode(k.toString())}
            >
              <Tab key="revenue" title="Выручка" />
              <Tab key="market" title="Оценка" />
            </Tabs>
            {chartMode === "market" && (
              <I18nProvider locale="ru-RU">
                <div className="flex items-center gap-2 ml-auto">
                  <DatePicker
                    aria-label="Дата от"
                    size="sm"
                    className="w-36"
                    value={parseDate(marketDateFrom)}
                    onChange={(d) => d && setMarketDateFrom(d.toString())}
                  />
                  <span className="text-foreground-400">—</span>
                  <DatePicker
                    aria-label="Дата до"
                    size="sm"
                    className="w-36"
                    value={parseDate(marketDateTo)}
                    onChange={(d) => d && setMarketDateTo(d.toString())}
                  />
                </div>
              </I18nProvider>
            )}
          </div>
          {chartMode === "revenue" ? (
            <SummaryChart month={activeTab} year={activeYear} />
          ) : marketHistory && marketHistory.steps.length > 0 ? (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
              <EstimateChart
                steps={marketHistory.steps}
                areaSqm={1}
                unitSuffix=" /м²"
              />
            </div>
          ) : (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4 flex items-center justify-center h-[260px] text-foreground-400">
              Нет данных по оценке для этой локации
            </div>
          )}
        </div>
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row gap-2">
            {/* @ts-ignore */}
            <NewLocationHandbook
              isFinancial
              errorMessage=""
              isInvalid={false}
              onSelectionChange={(key: any) => setActiveLocation(key)}
              selectedKey={activeLocation}
            />
            {/* @ts-ignore */}
            <NewYearHandbook
              errorMessage=""
              isInvalid={false}
              onSelectionChange={(key: any) => setActiveYear(key)}
              selectedKey={activeYear}
            />
            <Select
              label="Типы"
              placeholder="Выберите для фильтрации"
              selectionMode="multiple"
              className="max-w-xs"
              variant="bordered"
              selectedKeys={activeTypes}
              onSelectionChange={(keys) => {
                // @ts-expect-error - TODO: fix all the keys blyat
                setActiveTypes(keys);
              }}
            >
              {officeTypes.map((type) => (
                <SelectItem key={type.id}>{type.display_name}</SelectItem>
              ))}
            </Select>
          </div>
          <div className="flex flex-row gap-2">
            <Button
              color="primary"
              variant="flat"
              startContent={<FaDownload />}
              onClick={downloadReport}
            >
              Экспорт в Excel
            </Button>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  color="primary"
                  variant="flat"
                  startContent={<FaBars />}
                />
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem key="send-all" onClick={() => {}}>
                  Отправить счета всем
                </DropdownItem>
                <DropdownItem
                  key="sent-report"
                  onClick={() => {
                    downloadBillsReport();
                  }}
                >
                  Отчёт по выставленным счетам
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Tabs
            variant="light"
            selectedKey={activeTab}
            onSelectionChange={(e) => setActiveTab(e.toString())}
          >
            <Tab key="january" title="Январь" />
            <Tab key="february" title="Февраль" />
            <Tab key="march" title="Март" />
            <Tab key="april" title="Апрель" />
            <Tab key="may" title="Май" />
            <Tab key="june" title="Июнь" />
            <Tab key="july" title="Июль" />
            <Tab key="august" title="Август" />
            <Tab key="september" title="Сентябрь" />
            <Tab key="october" title="Октябрь" />
            <Tab key="november" title="Ноябрь" />
            <Tab key="december" title="Декабрь" />
          </Tabs>

          <div className="overflow-y-auto">
            <Table
              isHeaderSticky
              removeWrapper
              isStriped
              aria-label="Dynamic table"
            >
              <TableHeader columns={columns}>
                {(column) => (
                  <TableColumn key={column.key}>{column.label}</TableColumn>
                )}
              </TableHeader>

              <TableBody items={data?.rent_plan || []}>
                {(item) => (
                  <TableRow
                    key={`${item.office_id}_${item.rent_id}_${item.renter_id}`}
                    className="border-b-2 border-gray-300 dark:border-zinc-800"
                  >
                    {(columnKey) => (
                      <TableCell
                        key={`${item.office_id}_${item.rent_id}_${item.renter_id}_${columnKey}`}
                        className={`${columnKey == "renter" ? "max-w-64" : ""}`}
                      >
                        {getKeyValue(item, columnKey)}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
}
